
// Vue
import { Component, Vue, Prop, Emit, Watch } from "vue-property-decorator";

// Services
import mapSetupApi from '@/services/api.mapSetup.service';

// Components
import ManualGuardRow from "./dispatch/ManualGuardRow.vue";
import DragDropUpload from '@/components/form/DragDropUpload.vue';
import SureViewIcon from "@/components/SureViewIcon.vue";
import Stepper from "@/components/form/Stepper.vue";
import vselect3 from "vselect3";
import { ILayer, IMarker } from "@sureview/v2-mapping";

// Types
import {  MapItemData } from "@/store/map-layers/types";
import { Map, MapSetupLayer, MapSetupItem } from "@/store/map-setup/types";
import ConvertMapActionsRequest from '@/types/request/ConvertMapActionsRequest';
import { IMap, convertStringToLatLng, IEnhancedMapping } from "@sureview/v2-mapping";
import { Stage } from "@/types/componentTypes/StepperComponentTypes";

enum ProcessState {
	CHOOSE_MAP = 0,
	MAP_PLACEMENT = 1,
	PREVIEW = 2,
	REVIEW_CONFIRM = 3
}

@Component({
	components: {
		"manual-guard-row": ManualGuardRow,
		"drag-drop-upload": DragDropUpload,
		"v-select3": vselect3,
		"stepper": Stepper,
		"sureview-icon": SureViewIcon,
	}
})
export default class MapConversion extends Vue {
	// -- Props -- //

	/**
	 * @summary Prop which represents the legacy map to be converted.
	 */
	@Prop({ default: null, type: Object }) mapToConvert: Map;
	
	/**
	 * @summary Prop which represents the current selected map layer of which,
	 * the map conversion will take place.
	 */
	@Prop({ default: null, type: Object }) selectedLayer: MapSetupLayer;

	/**
	 * @summary Prop which represents the current map instance
	 */
	@Prop({ default: null, type: Object }) setupMap?: IMap | null;

	/**
	 * @summary Prop which represents the current enhanced map instance.
	 */
	@Prop({ default: null, type: Object }) enhancedMapping?: IEnhancedMapping | null;

	// -- Properties -- //

	private processState: typeof ProcessState = ProcessState;
	private selectedMap?: Map | any | null = null;
	private currentStage: number = this.processState.CHOOSE_MAP;
	private layerVisibility: boolean = true;
	private visible: boolean = false;
	private convertedMapLayerId: number = 0;
	private loading: boolean = false;
	private mapItems: IMarker[] = [];

	private stages: Stage[] = [
		{
			stage: this.processState.MAP_PLACEMENT,
			title: 'Map placement'
		},
		{
			stage: this.processState.PREVIEW,
			title: 'Preview'
		},
		{
			stage: this.processState.REVIEW_CONFIRM,
			title: 'Review and confirm'
		}
	];

	// -- Watchers -- //
	@Watch("mapToConvert")
	public onMapToConvertSelected(map: Map)
	{
		if (!map) {
			return;
		}

		this.selectedMap = map;
		this.$emit("map-conversion-started");
		this.nextStage();
	}

	// -- Computed Properties -- //

	/**
	 * @summary Computed property to get the current stage index to be used
	 * @returns {number} current tab index
	 */
	private get currentIndex(): number {
		return this.currentStage-1;
	}

	/**
	 * Dummy setter for our getter 'currentIndex'
	 * @returns {void}
	 */
	private set currentIndex(value: number) {}

	/**
	 * @summary Computed property which tells if we are in the finished state
	 * @returns {boolean} if the stepper is at the final stage
	 */
	private get hasFinished(): boolean {
		return this.convertedMapLayerId && !this.loading;
	}

	/**
	 * @summary Computed property which will gett the image data
	 * while appending the format type at the start.
	 * @returns {string} data and dataformat in a string
	 */
	private get getImage(): string {
		return `data:image/jpeg;base64,${this.selectedMap.picture}`;
	}

	// -- Emits -- //

	/**
	 * @summary Method / Event which will reset the component back to default values,
	 * and emit a reset for the map which will clear our the preview layer.
	 * @param visible state on which the modal will be in after reset
	 */
	@Emit("reset")
	private reset(visible: boolean = true): void {
		this.$emit("set-rotation-container-visibility", true);
		this.currentStage = 0;
		this.selectedMap = null;
		this.visible = false;
		this.mapItems.forEach(mapItem => mapItem.remove());
	}

	/**
	 * @summary Emit Method which will set the layer visibility
	 */
	@Emit('set-layer-visibility')
	private updateVisibility(visible: boolean = true): void {}

	/**
	 * @summary Emit Method which will;
	 * enabling or disabling of the map layer preview to be scaled/moved
	 */
	@Emit("toggle-layer-editable")
	@Emit("toggle-layer-clickable")
	private toggleMapLayerEditable(editable: boolean): void {}

	// -- Methods -- //

	/**
	 * @summary Method which deals with the final stage of the process
	 * @returns {Promise<void>} returns an promise
	 */
	private async finalStage(): Promise<void> {
		this.loading = true;
		const mapLayer: ILayer = {
			mapId: this.selectedMap.mapID,
			title: this.selectedMap.title,
			ne: this.selectedLayer.ne,
			sw: this.selectedLayer.sw,
			rotation: this.selectedLayer.rotation,
		};

		const { data } = await mapSetupApi.convertMap(mapLayer);
		this.convertedMapLayerId = data;
		this.$emit("reload-new-map-layer-for-group", this.convertedMapLayerId);
		this.loading = false;
	}

	/**
	 * @summary Method which will handle the preview stage
	 * @returns {Promise<void>} returns an promise
	 */
	private async previewState(): Promise<void> {
		this.loading = true;

		const payload: ConvertMapActionsRequest = {
			mapId: this.selectedMap.mapID,
			northEast: this.selectedLayer.ne,
			southWest: this.selectedLayer.sw,
			layerRotation: this.selectedLayer.rotation
		};

		const { data } = await mapSetupApi.PreviewConvertMap(payload);
		this.$emit('set-maplayer-items', data);
		this.setMapLayerItems(data);

		this.visible = false;
		this.loading = false;
	}

	/**
	 * @summary Method which will handle setting up and displaying the map layer items
	 * onto the preview layer.
	 * @returns {void}
	 */
	private setMapLayerItems(mapLayerItems: MapItemData[]): void {
		mapLayerItems.forEach(mapLayer => {
			const latLong = convertStringToLatLng(mapLayer.latLong);
			// create new map item
			const newItem: MapSetupItem = {
				mapLayerItemTypeID: mapLayer.mapLayerItemTypeID,
				title: mapLayer.title,
				latLong: `${latLong.lat} ${latLong.lng}`,
				mapLayerItemID: 0,
				minElevation: 0,
				maxElevation: 0,
				objectID: 0,
				extraValue: null,
				regionPath: null,
				mapLayerIds: [],
				saved: true,
				locationIdentifiers: [],
				brokenObjectLink: false
			};

			const marker: IMarker = this.enhancedMapping.createMarker(
				this.setupMap!, // Enhanced Mapping
				newItem.mapLayerItemID, // MapLayer Item Id
				null, // Event Record Id
				newItem.mapLayerItemTypeID, // Map Item Type Id
				newItem.objectID, // Object Id
				newItem.title, // Title
				convertStringToLatLng(newItem.latLong!), // Position
				newItem.regionPath, // Region Path
				newItem.minElevation, // Min Elevation
				newItem.maxElevation, // Max Elevation
				true // Visible
			);
			this.mapItems.push(marker);
		});
	}

	/**
	 * @summary Method which will handle going forward within the current stages
	 * @returns {Promise<void>} returns an promise
	 */
	private async nextStage(): Promise<void> {
		this.currentStage++;
		await this.switchStageLogic();
	}

	/**
	 * @summary Method which will trigger logic depending on the current stage
	 * @returns {Promise<void>} returns an promise
	 */
	private async switchStageLogic(): Promise<void> {
		switch (this.currentStage) {
			case this.processState.CHOOSE_MAP:
				// Do nothing, we should be at this stage initially.
				break;
			case this.processState.MAP_PLACEMENT:
				// Hide modal and load map image onto the map
				this.convertedMapLayerId = null;
				this.visible = false;
				this.$emit("map-placement", this.getImage);
				this.$emit("set-rotation-container-visibility", true);
				break;
			case this.processState.PREVIEW:
				// Fetch map layer items which were converted but not saved in the db.
				// used as a preview.
				this.visible = true;
				this.toggleMapLayerEditable(false);
				await this.previewState();
				this.$emit("set-rotation-container-visibility", false);
				break;
			case this.processState.REVIEW_CONFIRM:
				// Set the map and maplayerItems to be converted fully.
				this.visible = true;
				this.finalStage();
				this.$emit("set-rotation-container-visibility", true);
				break;
			default:
				// If this logic was hit, there was a problem
				// reset our stage to be beginning.
				this.reset(true);
				break;
		}
	}

	/**
	 * @summary Methoc which will reEaluate the map layer items, this is used if
	 * the map image is ajusted
	 * @returns {Promise<void>} returns an promise
	 */
	private async reEvaluateMapPlacement(): Promise<void> {
		// Clear map layer items off the map
		this.mapItems.forEach(mapItem => mapItem.remove());
		// Refetch the preview data
		this.currentStage--;

		this.toggleMapLayerEditable(true);
		this.$emit("set-rotation-container-visibility", true);
	}

	private cancel(): void {
		this.reset();
		this.layerVisibility = true;
		this.$emit("reset-rotation-container");
	}

	/**
	 * @summary Method which will reset the stages and,
	 * will close the modal.
	 * @returns {void} void
	 */
	private close(): void {
		this.reset(false);
	}

	/**
	 * @summary Method which will close the modal, however will stay
	 * on the desired stage, unless it's not part of the following stages;
	 * @enum {MAP_PLACEMENT, PREVIEW} processState
	 * @returns {void} void
	 */
	private hideModal(): void {
		// ignore, we are only placing the image on the map
		if (this.currentStage == this.processState.MAP_PLACEMENT || this.currentStage == this.processState.PREVIEW) return;
		this.reset(false);
		this.$emit("map-conversion-finished");
		this.currentStage = this.processState.CHOOSE_MAP;

		// If a converted map layer ID exists, it means conversion was successful, go to new layer
		if (this.convertedMapLayerId) {
			this.$emit("go-to-new-layer");
			this.convertedMapLayerId = null;
		}
	}

	private get submitBtnText(): string {
		return this.currentStage == this.processState.MAP_PLACEMENT ? "Preview" : "Save";
	}
}
